import { axiosApi } from 'src/CrudManager/Core/Utils/axios'
import { showErrorNotification } from 'src/CrudManager/Core/Utils/ToastNotification'
import type {
  ActionExecutionResult,
  GroupActionPayload,
  Notification,
  NotificationActionPayload,
  NotificationFilters,
  PaginatedNotifications,
} from '../types'

export function useNotificationApi() {
  const baseEndpoint = '/notifications'

  const fetchNotifications = async (
    page = 1,
    perPage = 20,
    filters?: NotificationFilters
  ): Promise<PaginatedNotifications> => {
    try {
      const params = new URLSearchParams()
      params.append('page', page.toString())
      params.append('per_page', perPage.toString())

      if (filters?.read !== null && filters?.read !== undefined) {
        params.append('read', filters.read.toString())
      }
      if (filters?.priority) {
        params.append('priority', filters.priority)
      }
      if (filters?.type) {
        params.append('type', filters.type)
      }
      if (filters?.progressive !== null && filters?.progressive !== undefined) {
        params.append('progressive', filters.progressive.toString())
      }
      if (filters?.mandatory !== null && filters?.mandatory !== undefined) {
        params.append('mandatory', filters.mandatory.toString())
      }

      const response = await axiosApi.get(`${baseEndpoint}?${params.toString()}`)
      return response.data as PaginatedNotifications
    } catch (error) {
      console.error('Failed to fetch notifications:', error)
      showErrorNotification('Failed to load notifications', error)
      throw error
    }
  }

  const getNotification = async (id: string): Promise<Notification> => {
    try {
      const response = await axiosApi.get(`${baseEndpoint}/${id}`)
      return response.data.data as Notification
    } catch (error) {
      console.error('Failed to fetch notification:', error)
      showErrorNotification('Failed to load notification', error)
      throw error
    }
  }

  const markNotificationAsRead = async (id: string): Promise<void> => {
    try {
      await axiosApi.patch(`${baseEndpoint}/${id}/read`)
    } catch (error) {
      console.error('Failed to mark notification as read:', error)
      showErrorNotification('Failed to mark notification as read', error)
      throw error
    }
  }

  const markNotificationAsUnread = async (id: string): Promise<void> => {
    try {
      await axiosApi.patch(`${baseEndpoint}/${id}/unread`)
    } catch (error) {
      console.error('Failed to mark notification as unread:', error)
      showErrorNotification('Failed to mark notification as unread', error)
      throw error
    }
  }

  const markAllNotificationsAsRead = async (): Promise<void> => {
    try {
      await axiosApi.patch(`${baseEndpoint}/mark-all-read`)
    } catch (error) {
      console.error('Failed to mark all notifications as read:', error)
      showErrorNotification('Failed to mark all notifications as read', error)
      throw error
    }
  }

  const deleteNotification = async (id: string): Promise<void> => {
    try {
      await axiosApi.delete(`${baseEndpoint}/${id}`)
    } catch (error) {
      console.error('Failed to delete notification:', error)
      showErrorNotification('Failed to delete notification', error)
      throw error
    }
  }

  const executeNotificationAction = async (
    payload: NotificationActionPayload
  ): Promise<ActionExecutionResult> => {
    try {
      const response = await axiosApi.post(
        `${baseEndpoint}/${payload.notification_id}/actions/${payload.action_name}`,
        payload.action_data || {}
      )
      return response.data as ActionExecutionResult
    } catch (error) {
      console.error('Failed to execute notification action:', error)
      showErrorNotification('Failed to execute action', error)
      throw error
    }
  }

  const executeGroupAction = async (
    payload: GroupActionPayload
  ): Promise<ActionExecutionResult> => {
    try {
      const response = await axiosApi.post(
        `${baseEndpoint}/groups/${payload.group_id}/actions/${payload.action_name}`,
        payload.action_data || {}
      )
      return response.data as ActionExecutionResult
    } catch (error) {
      console.error('Failed to execute group action:', error)
      showErrorNotification('Failed to execute group action', error)
      throw error
    }
  }

  const getUnreadCount = async (): Promise<number> => {
    try {
      const response = await axiosApi.get(`${baseEndpoint}/unread-count`)
      return response.data.count as number
    } catch (error) {
      console.error('Failed to get unread count:', error)
      return 0
    }
  }

  const getProgressiveNotifications = async (): Promise<Notification[]> => {
    try {
      const response = await axiosApi.get(`${baseEndpoint}/progressive`)
      return response.data.data as Notification[]
    } catch (error) {
      console.error('Failed to fetch progressive notifications:', error)
      showErrorNotification('Failed to load progressive notifications', error)
      throw error
    }
  }

  const getMandatoryNotifications = async (): Promise<Notification[]> => {
    try {
      const response = await axiosApi.get(`${baseEndpoint}/mandatory`)
      return response.data.data as Notification[]
    } catch (error) {
      console.error('Failed to fetch mandatory notifications:', error)
      showErrorNotification('Failed to load mandatory notifications', error)
      throw error
    }
  }

  const retryProgressiveAction = async (notificationId: string): Promise<ActionExecutionResult> => {
    try {
      const response = await axiosApi.post(`${baseEndpoint}/${notificationId}/retry`)
      return response.data as ActionExecutionResult
    } catch (error) {
      console.error('Failed to retry progressive action:', error)
      showErrorNotification('Failed to retry action', error)
      throw error
    }
  }

  const cancelProgressiveNotification = async (notificationId: string): Promise<void> => {
    try {
      await axiosApi.post(`${baseEndpoint}/${notificationId}/cancel`)
    } catch (error) {
      console.error('Failed to cancel progressive notification:', error)
      showErrorNotification('Failed to cancel notification', error)
      throw error
    }
  }

  const getNotificationStats = async () => {
    try {
      const response = await axiosApi.get(`${baseEndpoint}/stats`)
      return response.data
    } catch (error) {
      console.error('Failed to fetch notification stats:', error)
      return {
        total: 0,
        unread: 0,
        progressive: 0,
        mandatory: 0,
        by_priority: { low: 0, normal: 0, high: 0, urgent: 0 },
      }
    }
  }

  return {
    fetchNotifications,
    getNotification,
    markNotificationAsRead,
    markNotificationAsUnread,
    markAllNotificationsAsRead,
    deleteNotification,
    executeNotificationAction,
    executeGroupAction,
    getUnreadCount,
    getProgressiveNotifications,
    getMandatoryNotifications,
    retryProgressiveAction,
    cancelProgressiveNotification,
    getNotificationStats,
  }
}
